export const EMPTY_PRODUCT_CREATION_PROGRESS = Object.freeze({
  productDraftKey: null,
  product: null,
  completedVarieties: Object.freeze({}),
});

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const getDraftKey = (draft, entityLabel) => {
  const draftKey = String(draft?._draftKey ?? '').trim();
  if (!draftKey) {
    throw new Error(`${entityLabel} necesita una clave de borrador estable.`);
  }
  return draftKey;
};

const getVarietyDrafts = (productDraft, varietyDrafts) => {
  if (Array.isArray(varietyDrafts)) return varietyDrafts;
  if (varietyDrafts !== undefined && varietyDrafts !== null) {
    throw new Error('Las variedades deben ser una lista.');
  }
  if (Array.isArray(productDraft?.varietyDrafts)) return productDraft.varietyDrafts;
  if (Array.isArray(productDraft?.varieties)) return productDraft.varieties;
  if (Array.isArray(productDraft?.Varieties)) return productDraft.Varieties;
  return [];
};

const getFormatDrafts = (varietyDraft) => {
  if (Array.isArray(varietyDraft?.formatDrafts)) return varietyDraft.formatDrafts;
  if (Array.isArray(varietyDraft?.formats)) return varietyDraft.formats;
  if (Array.isArray(varietyDraft?.VarietyPrices)) return varietyDraft.VarietyPrices;
  return [];
};

const validateDraftTree = (productDraft, varietyDrafts) => {
  const productDraftKey = getDraftKey(productDraft, 'El producto');
  const seenVarietyKeys = new Set();
  const varieties = varietyDrafts.map((varietyDraft) => {
    const varietyDraftKey = getDraftKey(varietyDraft, 'Cada variedad');
    if (seenVarietyKeys.has(varietyDraftKey)) {
      throw new Error('Las claves de borrador de las variedades deben ser únicas.');
    }
    seenVarietyKeys.add(varietyDraftKey);

    const seenFormatKeys = new Set();
    const formats = getFormatDrafts(varietyDraft).map((formatDraft) => {
      const formatDraftKey = getDraftKey(formatDraft, 'Cada formato');
      if (seenFormatKeys.has(formatDraftKey)) {
        throw new Error('Las claves de borrador de los formatos deben ser únicas dentro de cada variedad.');
      }
      seenFormatKeys.add(formatDraftKey);
      return { draft: formatDraft, draftKey: formatDraftKey };
    });

    return { draft: varietyDraft, draftKey: varietyDraftKey, formats };
  });

  return { productDraftKey, varieties };
};

const cloneCompletedVarieties = (completedVarieties) => Object.fromEntries(
  Object.entries(
    completedVarieties && typeof completedVarieties === 'object' && !Array.isArray(completedVarieties)
      ? completedVarieties
      : {}
  ).map(([draftKey, entry]) => [
    draftKey,
    {
      ...(entry || {}),
      completedFormats: {
        ...(entry?.completedFormats && typeof entry.completedFormats === 'object'
          && !Array.isArray(entry.completedFormats)
          ? entry.completedFormats
          : {}),
      },
    },
  ])
);

export const resumeProductCreation = async ({
  progress = EMPTY_PRODUCT_CREATION_PROGRESS,
  productDraft,
  varietyDrafts,
  createProduct,
  createVariety,
  createFormat,
  onProgress,
} = {}) => {
  const drafts = getVarietyDrafts(productDraft, varietyDrafts);
  const validated = validateDraftTree(productDraft, drafts);
  let nextProgress = {
    productDraftKey: progress?.productDraftKey ?? null,
    product: progress?.product ?? null,
    completedVarieties: cloneCompletedVarieties(progress?.completedVarieties),
  };

  if (nextProgress.productDraftKey
    && nextProgress.productDraftKey !== validated.productDraftKey) {
    throw new Error('El progreso guardado pertenece a otro borrador de producto.');
  }

  if (!nextProgress.product) {
    const product = await createProduct(productDraft);
    nextProgress = {
      ...nextProgress,
      productDraftKey: validated.productDraftKey,
      product,
    };
    onProgress?.(nextProgress);
  } else if (!nextProgress.productDraftKey) {
    nextProgress = { ...nextProgress, productDraftKey: validated.productDraftKey };
  }

  for (const varietyDraftEntry of validated.varieties) {
    const { draft: varietyDraft, draftKey: varietyDraftKey } = varietyDraftEntry;
    let varietyProgress = nextProgress.completedVarieties[varietyDraftKey] || null;

    if (!varietyProgress?.variety) {
      const variety = await createVariety(varietyDraft, nextProgress.product);
      varietyProgress = {
        ...(varietyProgress || {}),
        variety,
        completedFormats: typeof createFormat === 'function'
          ? { ...(varietyProgress?.completedFormats || {}) }
          : Object.fromEntries(varietyDraftEntry.formats.map(({ draftKey }, index) => [draftKey, variety.VarietyPrices?.[index]])),
      };
      nextProgress = {
        ...nextProgress,
        completedVarieties: {
          ...nextProgress.completedVarieties,
          [varietyDraftKey]: varietyProgress,
        },
      };
      onProgress?.(nextProgress);
    }

    for (const formatDraftEntry of typeof createFormat === 'function' ? varietyDraftEntry.formats : []) {
      const { draft: formatDraft, draftKey: formatDraftKey } = formatDraftEntry;
      if (hasOwn(varietyProgress.completedFormats, formatDraftKey)) continue;

      const format = await createFormat(
        formatDraft,
        varietyProgress.variety,
        nextProgress.product
      );
      varietyProgress = {
        ...varietyProgress,
        completedFormats: {
          ...varietyProgress.completedFormats,
          [formatDraftKey]: format,
        },
      };
      nextProgress = {
        ...nextProgress,
        completedVarieties: {
          ...nextProgress.completedVarieties,
          [varietyDraftKey]: varietyProgress,
        },
      };
      onProgress?.(nextProgress);
    }
  }

  const assembledVarieties = validated.varieties.map((varietyDraftEntry) => {
    const varietyProgress = nextProgress.completedVarieties[varietyDraftEntry.draftKey];
    return {
      ...varietyProgress.variety,
      VarietyPrices: typeof createFormat === 'function' ? varietyDraftEntry.formats.map(
        ({ draftKey }) => varietyProgress.completedFormats[draftKey]
      ) : (varietyProgress.variety.VarietyPrices || []),
    };
  });

  return {
    ...nextProgress,
    product: {
      ...nextProgress.product,
      Varieties: assembledVarieties,
    },
  };
};
